import { faImage } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useTranslation } from '../providers/TranslationProvider';

/**
 * Shared placeholder for a missing / failed image across the ecommerce
 * storefront (and any other web-ui image surface).
 *
 * Theme-agnostic by design: it does NOT reference brand-* Tailwind tokens
 * (those only exist in the storefront's Tailwind config) so it renders
 * correctly everywhere web-ui is consumed, including the CRM. The icon tone is
 * black/white-with-opacity, which stays legible on BOTH light and dark
 * backgrounds — fixing the old `dark:text-gray-600`-on-teal fallback that was
 * effectively invisible in dark mode. It fills its container (w-full h-full)
 * and inherits the container's background, so callers keep their own surface
 * color (e.g. bg-brand-input).
 */
const SIZE_CLASSES = {
	sm: 'text-2xl',
	md: 'text-4xl',
	lg: 'text-6xl',
} as const;

interface ImagePlaceholderProps {
	/** Icon scale relative to the container. Defaults to `md` (product card). */
	size?: keyof typeof SIZE_CLASSES;
	/** Optional caption rendered (translated) below the icon. Omitted by default. */
	label?: string;
	/** Extra classes merged onto the root wrapper. */
	className?: string;
	/** Override the icon's color / size classes. */
	iconClassName?: string;
}

export function ImagePlaceholder({
	size = 'md',
	label,
	className,
	iconClassName,
}: ImagePlaceholderProps): JSX.Element {
	const { t } = useTranslation();

	return (
		<div
			role="img"
			aria-label={t('No image available')}
			className={clsx(
				'flex flex-col items-center justify-center w-full h-full gap-1 select-none',
				className,
			)}
		>
			<FontAwesomeIcon
				icon={faImage}
				className={clsx(
					SIZE_CLASSES[size],
					'text-black/25 dark:text-white/40',
					iconClassName,
				)}
			/>
			{label != null && label !== '' && (
				<span className="text-xs font-medium text-black/40 dark:text-white/50">
					{t(label)}
				</span>
			)}
		</div>
	);
}
